import { Trash2, X, MapPin, Pencil, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CartItem } from "../../types/product.types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import { User } from "../../types/slice.types";

interface ShoppingCartProps {
  cart: CartItem[];
  setIsCartOpen: (isOpen: boolean) => void;
  removeFromCart: (_id: string) => void;
  handleProductOrder: (addressId: string) => void;
}

export const ShoppingCart = ({
  cart,
  setIsCartOpen,
  removeFromCart,
  handleProductOrder,
}: ShoppingCartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth.user) as User;
  const addresses = user?.addresses || [];

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setIsCartOpen(false)}
      />

      <div className="relative mt-16 w-full max-w-5xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-5">
          <h2 className="text-xl font-semibold text-primaryText dark:text-primaryTextDark">
            {t("cart") || "Cart"} ({cart.length})
          </h2>

          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden px-6 pb-6 gap-8">
          <div className="w-1/2 overflow-y-auto custom-scroll space-y-4">
            {cart.map((item) => (
              <div
                key={item._id}
                className="flex justify-between items-center p-4 rounded-lg bg-gray-50 dark:bg-gray-800/40"
              >
                <div>
                  <p className="font-medium text-primaryText dark:text-primaryTextDark">
                    {item.name} {item.quantity > 1 && `x ${item.quantity}`}
                  </p>
                  <p className="text-sm text-gray-500">₹{item.price}</p>
                </div>

                <button
                  onClick={() => removeFromCart(item._id)}
                  className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition"
                >
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </div>
            ))}

            {cart.length === 0 && (
              <p className="text-center mt-10 text-gray-500">
                {t("cartEmpty") || "Cart is empty."}
              </p>
            )}
          </div>

          <div className="w-1/2 space-y-4">
            <h3 className="text-lg font-semibold text-primaryText dark:text-primaryTextDark">
              {t("Select Address") || "Select Address"}
            </h3>

            {addresses.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 mt-12">
                <p className="text-sm text-gray-500">
                  {t("No saved addresses found")}
                </p>

                <button
                  onClick={() => navigate("/profile")}
                  className="px-5 py-2 rounded-lg bg-secondaryText/80 dark:bg-secondaryTextDark/80 hover:bg-secondaryText hover:dark:bg-secondaryTextDark transition"
                >
                  {t("Add Address")}
                </button>
              </div>
            )}

            {addresses.map((addr) => (
              <button
                key={addr._id}
                onClick={() => setSelectedAddressId(addr._id as string)}
                className={`w-full text-left p-4 rounded-lg transition ${
                  selectedAddressId === addr._id
                    ? "bg-green-100/70 dark:bg-green-900/25 ring-2 ring-green-500"
                    : "bg-gray-50 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                <div className="flex gap-3">
                  <MapPin size={18} className="mt-1 text-green-600" />
                  <div>
                    <p className="font-medium text-primaryText dark:text-primaryTextDark">
                      {addr.building}, {addr.street}
                    </p>
                    <p className="text-sm text-gray-500">
                      {addr.city}, {addr.country}
                    </p>
                    <p className="text-sm text-gray-500">{addr.phone}</p>
                  </div>
                </div>
              </button>
            ))}

            {addresses.length > 0 && (
              <div className="flex items-center gap-4 pt-2">
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                >
                  <Pencil size={14} />
                  {t("Edit addresses")}
                </button>

                {addresses.length < 2 && (
                  <button
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-1 text-sm text-green-600 hover:underline"
                  >
                    <Plus size={14} />
                    {t("Add address")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 pb-6">
          <button
            disabled={!selectedAddressId || cart.length === 0}
            onClick={() => handleProductOrder(selectedAddressId as string)}
            className="w-full py-3 rounded-lg bg-secondaryText/80 dark:bg-secondaryTextDark/80 hover:bg-secondaryText hover:dark:bg-secondaryTextDark disabled:opacity-40 disabled:cursor-not-allowed transition text-primaryText font-medium"
          >
            {t("checkout")} ₹
            {cart
              .reduce(
                (total, item) => total + item.price * (item.quantity || 1),
                0
              )
              .toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
};
